import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchMachine,
  fetchMachineOutTask,
  fetchMachines,
  selectCurrentMachine,
  selectMachineLoading,
} from '../../features/machines/machinesSlice';
import { fetchMachinesWithDependencies } from '../../helpers/fetchData';
import { useUpdateMutation } from '../../app/services/machines';
import { useAuth } from '../../hooks/useAuth';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import ConfirmDiscardDialog from '../../components/ConfirmDiscardDialog';
import { ACTIONS } from '../../constants/actionTables';
import TableComponent from '../../components/Table';
import ModalAddValidDoc from '../../components/ModalAddValidDoc';
import ValidationStatusBlock from '../../components/ValidationStatusBlock';
import ModalReportRepair from '../../components/ModalReportRepair';
import MachineProfileInputs from '../../components/MachineProfileInputs';
import ModalConfirmStatusChange from '../../components/ModalConfirmStatusChange';
import UpcomingMaintenanceCalendar from '../../components/UpcomingMaintenanceCalendar';
import CustomTabs from '../../components/CustomTabs';
import { formatDate } from '../../helpers/formatDate';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip, Typography, CircularProgress, Box } from '@mui/material';

import styles from './style.module.css';
import Toast from '../../components/Toast';
import MachineProfileHeader from '../../components/MachineProfileHeader';

const initialForm = {
  deputy: '',
  description: '',
  location: '',
  machineGroup: '',
  name: '',
  number: '',
  responsiblePerson: '',
  serialNumber: '',
  softwareVersion: '',
  yearOfManufacture: '',
  status: '',
};

function MachineProfile() {
  const [form, setForm] = useState(initialForm);
  const [originalForm, setOriginalForm] = useState(initialForm);
  const isDirty = JSON.stringify(form) !== JSON.stringify(originalForm);
  const { confirmProps } = useUnsavedChangesWarning(isDirty);
  const [validationsDoc, setValidationsDoc] = useState(null);
  const [manualsDoc, setManualsDoc] = useState(null);
  const [maintenanceTasks, setMaintenanceTasks] = useState(null);
  const [suppliesData, setSuppliesData] = useState(null);
  const [imageLink, setImageLink] = useState();
  const [isInputsDisabled, setIsInputsDisabled] = useState(false);
  const [isStatusChange, setIsStatusChange] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [isTasksLoading, setIsTasksLoading] = useState(true);

  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [updateMachine] = useUpdateMutation();
  const dispatch = useDispatch();

  // Get machine data from Redux store
  const machine = useSelector(selectCurrentMachine);
  const isLoading = useSelector(selectMachineLoading);

  const titlesDocuments = [t('generic.document'), t('generic.approvalDate'), t('generic.action')];
  const titlesSupplies = [
    t('generic.number'),
    t('generic.name'),
    t('generic.description'),
    t('generic.manufacture'),
    t('generic.safetySheet'),
  ];
  const titlesManuals = [t('generic.document'), t('generic.description'), t('generic.action')];

  // Load machine data and dependencies when component mounts or ID changes
  useEffect(() => {
    // Load dependencies (groups, locations, maintenance types, users)
    fetchMachinesWithDependencies(dispatch);
    // Load specific machine data
    dispatch(fetchMachine(id));
  }, [id, dispatch]);

  // Process machine data when it's loaded from Redux
  useEffect(() => {
    if (machine) {
      const hydrated = {
        id,
        deputy: machine.deputy,
        description: machine.description,
        location: machine.location,
        machineGroup: machine.machine_group,
        name: machine.name,
        number: machine.number,
        responsiblePerson: machine.responsible_person,
        serialNumber: machine.serial_number,
        softwareVersion: machine.software_version || '',
        yearOfManufacture: machine.year_of_manufacture,
        status: machine.status,
        is_archived: machine.is_archived,
        activated_at: machine.activated_at,
      };
      setForm(hydrated);
      setOriginalForm(hydrated);
      setImageLink(machine.machine_group?.image);
      setManualsDoc(
        machine.machine_group?.manuals
          ?.filter((doc) => doc && doc.filename) // Filter out null/incomplete manuals
          .map((doc) => ({
            document: doc.original_filename || '-',
            description: doc.description || '-',
            link: doc.filename,
          })) || []
      );
      setValidationsDoc(
        machine.validation_documents
          ?.filter((doc) => doc && doc.id) // Filter out null/incomplete docs
          .map((doc) => ({
            id: doc.id,
            document: doc.name || '-',
            date: doc.approval_date ? formatDate(doc.approval_date) : '-',
            link: doc.filename,
          })) || []
      );
      setSuppliesData(
        machine.machine_group?.machine_supplies
          ?.filter((supply) => supply.supply) // Filter out null supplies
          .map((supply) => ({
            number: supply.supply.id,
            name: supply.supply.name,
            description: supply.supply.description,
            manufacture: supply.supply.manufacture,
            link: supply.supply.filename,
          })) || []
      );
    }
  }, [machine, id]);

  // Create fetchData function for child components that need to refresh machine data
  const fetchData = useCallback(async () => {
    await dispatch(fetchMachine(id));
  }, [dispatch, id]);

  const fetchMachineTask = useCallback(async () => {
    if (machine?.id) {
      try {
        setIsTasksLoading(true);
        const response = await dispatch(fetchMachineOutTask(machine.id));
        if (response.payload) {
          setMaintenanceTasks(response.payload);
        }
      } catch (error) {
        console.error('Error fetching machine tasks:', error);
      } finally {
        setIsTasksLoading(false);
      }
    }
  }, [dispatch, machine?.id]);

  useEffect(() => {
    fetchMachineTask();
  }, [fetchMachineTask]);

  const handleSubmit = async () => {
    try {
      const { activated_at, ...rest } = form;
      const machineData = {
        ...rest,
        activatedAt: activated_at,
      };

      if (form.status === machine.status) {
        await updateMachine({ id, machineData });
        await dispatch(fetchMachine(id));
        await dispatch(fetchMachines());
        setIsInputsDisabled(false);
      } else {
        setIsStatusChange(true);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleCancel = async () => {
    try {
      await fetchData();
      setIsInputsDisabled(false);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const [toastOpen, setToastOpen] = useState(false);
  const handleToastClose = () => {
    setToastOpen(false);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const tabs = [
    {
      key: 'details',
      label: t('generic.details'),
      onClick: () => handleTabChange('details'),
    },
    {
      key: 'documents',
      label: t('generic.documents'),
      onClick: () => handleTabChange('documents'),
    },
  ];

  // Show loading state while machine data is being fetched
  if (isLoading) {
    return (
      <section className={styles.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <div style={{ textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              {t('generic.loadingData')}
            </Typography>
          </div>
        </Box>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      <MachineProfileHeader
        form={form}
        user={user}
        isInputsDisabled={isInputsDisabled}
        setIsInputsDisabled={setIsInputsDisabled}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        id={id}
        setToastOpen={setToastOpen}
        setForm={setForm}
      />

      <CustomTabs tabs={tabs} activeTab={activeTab} />
      <div className={styles.specific}>
        {activeTab === 'details' ? (
          <>
            <div className={styles.fields_inputs}>
              <div className={styles.account_info}>
                <Typography variant="h6" fontWeight="bold">
                  {t('generic.machine')}
                </Typography>

                <Tooltip
                  title={<Typography fontWeight="bold">{t('machineList.clickEdit')}</Typography>}
                  placement="top"
                  arrow
                >
                  <InfoOutlinedIcon color="primary" />
                </Tooltip>
              </div>
              <img
                src={`${process.env.REACT_APP_BASE_URL}machine-groups/image/${imageLink}`}
                alt=""
                style={{ maxWidth: '305px', maxHeight: '305px', objectFit: 'contain' }}
              />

              <MachineProfileInputs
                form={form}
                setForm={setForm}
                isInputsDisabled={isInputsDisabled}
                isDataLoaded={!!machine}
              />
            </div>

            <div className={styles.fields_maintenance}>
              <UpcomingMaintenanceCalendar
                maintenanceTasks={machine?.status === 'active' ? maintenanceTasks || [] : []}
                fetchData={fetchMachineTask}
                machineGroupName={form.machineGroup?.name}
                machineGroupImg={imageLink}
                isLoading={isTasksLoading}
              />
              {machine?.status === 'active' ? (
                <ModalReportRepair
                  id={machine.id}
                  machineName={machine.name}
                  machineNumber={machine.number}
                  groupName={machine.machine_group.name}
                  groupImg={machine.machine_group.image}
                />
              ) : (
                <Tooltip title={t('machineList.posiibleOnly')} placement="top" arrow>
                  <div
                    style={{
                      display: 'flex',
                      boxSizing: 'border-box',
                      alignItems: 'center',
                      justifyContent: 'center',
                      alignSelf: 'flex-end',
                      height: '42px',
                      width: '171px',
                      backgroundColor: 'transparent',
                      color: '#a7a7a7',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: '4px',
                      cursor: 'not-allowed',
                    }}
                  >
                    {t('modals.reportRepair')}
                  </div>
                </Tooltip>
              )}
            </div>
          </>
        ) : (
          <div className={styles.double_fields}>
            {manualsDoc && (
              <div className={styles.fields}>
                <Typography fontWeight="bold">{t('generic.manuals')}</Typography>
                <TableComponent
                  search={false}
                  titles={titlesManuals}
                  data={manualsDoc}
                  action={ACTIONS.SPECIFIC_MACHINE_MANUALS}
                />
              </div>
            )}

            <div className={styles.fields}>
              <div className={styles.sub_header}>
                <Typography fontWeight="bold">{t('machineList.validationDocuments')}</Typography>
                {user?.role === 'admin' && <ModalAddValidDoc id={id} fetchData={fetchData} />}
              </div>
              <ValidationStatusBlock machine={machine} t={t} />
              {validationsDoc && (
                <TableComponent
                  search={false}
                  titles={titlesDocuments}
                  data={validationsDoc}
                  fetchData={fetchData}
                  action={ACTIONS.SPECIFIC_MACHINE_DOCUMENTS}
                />
              )}
            </div>

            <div className={styles.fields}>
              <Typography fontWeight="bold">{t('generic.supplies')}</Typography>
              {suppliesData && (
                <TableComponent
                  search={false}
                  titles={titlesSupplies}
                  data={suppliesData}
                  action={ACTIONS.SPECIFIC_MACHINE_SUPPLY}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <ModalConfirmStatusChange
        isStatusChange={isStatusChange}
        setIsStatusChange={setIsStatusChange}
        setIsInputsDisabled={setIsInputsDisabled}
        fetchData={fetchData}
        id={id}
        form={form}
      />
      <Toast
        open={toastOpen}
        handleClose={handleToastClose}
        severity="success"
        message={t('message.machineArchivedNotification')}
      />
      <ConfirmDiscardDialog {...confirmProps} />
    </section>
  );
}

export default MachineProfile;
